// src/contexts/PermissionContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { fetchWithAuth } from "../utils/fetchWithAuth";

// ============================================================================
// PERMISSION DEFINITIONS — a FALLBACK, not the source of truth
// ============================================================================
//
// These used to be the source of truth on the client, and the header said
// "must match backend". It stopped matching: the two Time Entry roles were
// added to the backend's ROLE_PERMISSIONS and never copied here, so anybody on
// one of those roles got `new Set()` in the SPA and could see nothing, which is
// the exact bug the backend fix was supposed to have cured.
//
// /api/accounts/me/ has always returned `organization.permissions`, computed
// from the backend's own dict. loadPermissions now uses that, so the two copies
// cannot drift again. What is left here runs only if the payload is missing the
// array (an older API than this bundle), so it is kept correct but it is no
// longer what decides anything.

const ROLE_HIERARCHY = [
  "Owner",
  "Admin",           // Can display as "VP"
  "Department Leader",
  "Manager",
  "Member",
  "Authorized User",
  // Narrower than Authorized User: somebody whose whole relationship with the
  // platform is recording their hours. Order matters, this list is ranked.
  "Time Entry - Manual",
  "Time Entry - Clock",
];

const ROLE_PERMISSIONS = {
  Owner: new Set([
    "view_team_page",
    "edit_team_page",
    "view_org_management",
    "edit_org_management",
    "view_income_statement",
    "view_stripe_settings",
    "edit_stripe_settings",
    "view_all_departments",
    "view_all_contracts",
    "view_all_timecards",
    "approve_timecards",
    "view_all_travel",
    "approve_travel",
    "view_all_milestones",
    "approve_milestones",
    "manage_subscriptions",
    "transfer_ownership",
    "invite_members",
    "remove_members",
    "change_member_roles",
    "create_departments",
    "manage_departments",
    "delete_departments",
    "view_finance",
    "view_transactions",
    "draft_contracts",
    "approve_contracts",
    "sign_contracts",  // Only Owner can sign
    "view_fraud_alerts",
    "view_talent_search",
    "send_messages",
  ]),
  Admin: new Set([
    "view_team_page",
    "edit_team_page",
    "view_org_management",
    "edit_org_management",
    "view_income_statement",
    // No Stripe settings: the company's Stripe account is the Owner's.
    "view_all_departments",
    "view_all_contracts",
    "view_all_timecards",
    "approve_timecards",
    "view_all_travel",
    "approve_travel",
    "view_all_milestones",
    "approve_milestones",
    "manage_subscriptions",
    "invite_members",
    "remove_members",
    "change_member_roles",
    "create_departments",
    "manage_departments",
    "view_finance",
    "view_transactions",
    "draft_contracts",
    "approve_contracts",
    // No sign_contracts - only Owner signs
    "view_fraud_alerts",
    "view_talent_search",
    "send_messages",
  ]),
  "Department Leader": new Set([
    "view_department_contracts",
    "view_department_timecards",
    "approve_timecards",
    "view_department_travel",
    "approve_travel",
    "view_department_milestones",
    "approve_milestones",
    "invite_members_to_department",
    "draft_contracts",
    "approve_contracts",
    "view_talent_search",
    "send_messages",
    "view_department_finance",
  ]),
  Manager: new Set([
    "manage_departments",
    "view_department_contracts",
    "view_department_timecards",
    "approve_timecards",
    "view_department_travel",
    "approve_travel",
    "view_department_milestones",
    "draft_contracts",
    "approve_contracts",
    "view_talent_search",
    "send_messages",
  ]),
  Member: new Set([
    // Member = regular team member, sees DEPARTMENT data
    "view_department_contracts",
    "view_department_timecards",
    "view_department_travel",
    "view_department_milestones",
    "view_talent_search",
    "draft_contracts",  // Can create drafts, sent for approval
    "send_messages",
  ]),
  "Authorized User": new Set([
    // Authorized User = external/limited, sees only OWN data
    "view_own_contracts",
    "view_own_timecards",
    "view_own_travel",
    "view_own_milestones",
    "view_talent_search",
    "draft_contracts",  // Can create drafts, sent for approval
    "send_messages",
  ]),
  // Mirrors accounts/permissions.py ROLE_PERMISSIONS exactly. Deliberately
  // WITHOUT view_talent_search and draft_contracts: an agency's clock-in worker
  // has no business browsing the talent pool or drafting a contract.
  "Time Entry - Manual": new Set([
    "view_own_contracts",
    "view_own_timecards",
    "submit_timecards",
    "send_messages",
  ]),
  "Time Entry - Clock": new Set([
    "view_own_contracts",
    "view_own_timecards",
    "submit_timecards",
    // The only difference from Manual: clocks in and out against a live timer
    // rather than typing hours after the fact. Both submit.
    "clock_in_out",
    "send_messages",
  ]),
};

// Pages mapped to required permissions (user needs ANY of these)
const PAGE_PERMISSIONS = {
  team_page: ["view_team_page"],
  org_management: ["view_org_management"],
  income_statement: ["view_income_statement"],
  stripe_settings: ["view_stripe_settings"],
  finance: ["view_finance", "view_department_finance"],
  contracts: ["view_all_contracts", "view_department_contracts", "view_own_contracts"],
  create_contract: ["draft_contracts"],  // Everyone can draft
  approve_contract: ["approve_contracts"],  // Managers+ can approve
  sign_contract: ["sign_contracts"],  // Only Owner
  timecards: ["view_all_timecards", "view_department_timecards", "view_own_timecards"],
  travel: ["view_all_travel", "view_department_travel", "view_own_travel"],
  milestones: ["view_all_milestones", "view_department_milestones", "view_own_milestones"],
  talent_search: ["view_talent_search"],
  fraud_alerts: ["view_fraud_alerts"],
  transactions: ["view_transactions"],
  subscriptions: ["manage_subscriptions"],
  hire: ["draft_contracts"],  // Hire page = start contract draft
};

// ============================================================================
// CONTEXT
// ============================================================================

const PermissionContext = createContext({
  role: null,
  departmentId: null,
  permissions: new Set(),
  isOrgWide: false,
  isDepartmentScoped: false,
  feeBps: 1000,
  feeRate: 0.10,
  // Whether a payer may be offered bank transfer. Server-driven: it is an
  // environment flag, and offering a rail that is not live would price a card
  // payment at the bank rate.
  achAvailable: false,
  loading: true,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  canAccessPage: () => false,
  canViewDepartmentData: () => false,
  refreshPermissions: () => {},
});

export function PermissionProvider({ children }) {
  const [permState, setPermState] = useState({
    role: null,
    departmentId: null,
    permissions: new Set(),
    isOrgWide: false,
    isDepartmentScoped: false,
    feeBps: 1000,
    feeRate: 0.10,
    achAvailable: false,
    loading: true,
  });

  const loadPermissions = useCallback(async () => {
    try {
      const res = await fetchWithAuth("/api/accounts/me/");
      const data = res.json ? await res.json() : res;

      const role = data?.organization?.role || null;
      const departmentId = data?.organization?.department_id || null;
      const feeBps = Number(data?.organization?.effective_fee_bps ?? 1000);

      // Take the server's answer. It is computed from the backend's own
      // ROLE_PERMISSIONS, which is what actually gates every endpoint, so
      // deriving it again from a local copy could only ever disagree -- and did.
      // The local dict is the fallback for a payload that predates this field.
      const served = data?.organization?.permissions;
      const permissions = Array.isArray(served)
        ? new Set(served)
        : (ROLE_PERMISSIONS[role] || new Set());

      // Same reasoning for scope. `??` not `||`, so a legitimate false from the
      // server is not overwritten by the local guess.
      const isOrgWide = data?.organization?.is_org_wide
        ?? ["Owner", "Admin"].includes(role);
      const isDepartmentScoped = data?.organization?.is_department_scoped
        ?? ["Department Leader", "Manager", "Member", "Authorized User"].includes(role);

      setPermState({
        role,
        departmentId,
        permissions,
        isOrgWide,
        isDepartmentScoped,
        feeBps,
        feeRate: feeBps / 10000,
        achAvailable: !!data?.organization?.ach_available,
        loading: false,
      });
    } catch (err) {
      console.error("Failed to load permissions:", err);
      setPermState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Check if user has a specific permission
  const hasPermission = useCallback(
    (permission) => permState.permissions.has(permission),
    [permState.permissions]
  );

  // Check if user has any of the specified permissions
  const hasAnyPermission = useCallback(
    (perms) => perms.some((p) => permState.permissions.has(p)),
    [permState.permissions]
  );

  // Check if user can access a specific page
  const canAccessPage = useCallback(
    (pageKey) => {
      const required = PAGE_PERMISSIONS[pageKey];
      if (!required || required.length === 0) return true;
      return hasAnyPermission(required);
    },
    [hasAnyPermission]
  );

  // Check if user can view data from a specific department
  const canViewDepartmentData = useCallback(
    (deptId) => {
      if (permState.isOrgWide) return true;
      return permState.departmentId === deptId;
    },
    [permState.isOrgWide, permState.departmentId]
  );

  const value = {
    ...permState,
    hasPermission,
    hasAnyPermission,
    canAccessPage,
    canViewDepartmentData,
    refreshPermissions: loadPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

// Hook to use permissions
export function usePermissions() {
  return useContext(PermissionContext);
}

// ============================================================================
// PERMISSION GATE COMPONENTS
// ============================================================================

/**
 * Gate that only renders children if user has the specified permission.
 * 
 * Usage:
 *   <PermissionGate permission="view_team_page">
 *     <TeamPageContent />
 *   </PermissionGate>
 */
export function PermissionGate({ permission, children, fallback = null }) {
  const { hasPermission, loading } = usePermissions();
  
  if (loading) return null;
  if (!hasPermission(permission)) return fallback;
  return children;
}

/**
 * Gate that renders children if user has ANY of the specified permissions.
 * 
 * Usage:
 *   <AnyPermissionGate permissions={["view_all_timecards", "view_department_timecards"]}>
 *     <TimecardsLink />
 *   </AnyPermissionGate>
 */
export function AnyPermissionGate({ permissions, children, fallback = null }) {
  const { hasAnyPermission, loading } = usePermissions();
  
  if (loading) return null;
  if (!hasAnyPermission(permissions)) return fallback;
  return children;
}

/**
 * Gate that renders children if user can access the specified page.
 * 
 * Usage:
 *   <PageGate page="team_page">
 *     <TeamPage />
 *   </PageGate>
 */
export function PageGate({ page, children, fallback = null }) {
  const { canAccessPage, loading } = usePermissions();
  
  if (loading) return null;
  if (!canAccessPage(page)) return fallback;
  return children;
}

/**
 * Gate for org-wide roles only (Owner, Admin/VP).
 */
export function OrgWideGate({ children, fallback = null }) {
  const { isOrgWide, loading } = usePermissions();
  
  if (loading) return null;
  if (!isOrgWide) return fallback;
  return children;
}

/**
 * Gate for specific roles.
 * 
 * Usage:
 *   <RoleGate roles={["Owner", "Admin"]}>
 *     <AdminOnlyFeature />
 *   </RoleGate>
 */
export function RoleGate({ roles, children, fallback = null }) {
  const { role, loading } = usePermissions();
  
  if (loading) return null;
  if (!roles.includes(role)) return fallback;
  return children;
}

/**
 * Access denied fallback component.
 */
export function AccessDenied({ message = "You don't have permission to view this page." }) {
  return (
    <div className="min-h-[300px] flex items-center justify-center">
      <div className="text-center p-8 bg-[#111] border border-gray-800 rounded-xl max-w-md">
        <div className="text-red-400 text-4xl mb-4">🔒</div>
        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
        <p className="text-gray-400">{message}</p>
      </div>
    </div>
  );
}

export default PermissionContext;